import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import config from '@/config';
import { useFingerprint, FingerprintData } from '@/hooks/useFingerprint';

interface BanDetails {
  evidence: string;
  reason: string;
}

interface SubscriptionInfo {
  plan: string;
  status: string;
  maybeExpiryDate?: string;
}

// should be aligned with backend's UserInfo
interface User {
  id: string;
  email: string;
  name: string;
  countryCode: string;
  maybeBannedDetails?: BanDetails;
  maybeSubscriptionInfo?: SubscriptionInfo;
  maybePhone?: string;
  maybeAvatar?: string;
  maybeGenderInfoFE?: {
    maybeDeclared?: string;
    maybeLastUpdated?: string; // ISO 8601 timestamp
  };
  maybeAgeInfoFE?: {
    maybeDeclared?: string;
    maybeLastUpdated?: string; // ISO 8601 timestamp
    confidence?: string; // "high", "medium", "low", "none"
  };
  maybeLocationInfoFE?: {
    countryCode: string;
    confidence: string; // "high", "medium", "low", "none"
  };
}

interface LoginParams {
  code: string,
  redirectUri: string,
  browserLocale?: string // Fallback for location detection
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (loginParams?: LoginParams) => void;
  logout: () => void;
  getIdToken: () => string | null;
  getCsrfToken: () => string | null;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

const jwtAuthKey = config.google.oauth.jwtAuthKey;
const csrfTokenKey = 'csrf_token';

// !!important - don't use state updates here
const refreshToken = async (): Promise<string | null> => {
  // setIsLoading(true);
  const headers: HeadersInit = {};
  // Only add ngrok header in development
  if (import.meta.env.VITE_APP_ENV === 'development') {
    headers['ngrok-skip-browser-warning'] = 'true';
  }

  const response = await fetch(`${config.baseUrl}/${config.endpoints.auth.google.refreshToken}`, {
    method: 'GET',
    credentials: 'include',
    headers
  });

  if (!response.ok) {
    console.error('Token refresh failed');
    localStorage.removeItem(jwtAuthKey);
    localStorage.removeItem(csrfTokenKey);
    return null;
  }

  // Extract CSRF token from response header
  const csrfToken = response.headers.get('X-CSRF-Token');
  if (csrfToken) {
    localStorage.setItem(csrfTokenKey, csrfToken);
  }

  const data = await response.json();

  if (data.status === 'success' && data.payload?.accessToken) {
    localStorage.setItem(jwtAuthKey, data.payload.accessToken);
    // await verifyToken(data.payload.accessToken);
    return data.payload.accessToken;
  }

  return null;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ isAuthenticated, setIsAuthenticated ] = useState<boolean>(false);
  const [ user, setUser ] = useState<User | null>(null);
  const [ error, setError ] = useState<string | null>(null);
  const [ isLoading, setIsLoading ] = useState(true);

  // Fingerprint hook for location verification
  const { getFingerprint } = useFingerprint();

  const verifyToken = useCallback(async (token: string) => {
    setError(null);
    setIsLoading(true);

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    // Only add ngrok header in development
    if (import.meta.env.VITE_APP_ENV === 'development') {
      headers['ngrok-skip-browser-warning'] = 'true';
    }

    const response = await fetch(`${config.baseUrl}/${config.endpoints.auth.google.verify}`, {
      method: 'GET',
      headers
    });

    // todo: wrap in a try-catch
    if (!response.ok) {
      // Invalid token - remove it
      localStorage.removeItem(jwtAuthKey);
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      setError('Session expired. Please login again.');
      return;
    }

    const data = await response.json();

    if (data.status === 'success' && data.payload?.user) {
      setUser(data.payload.user);
      setIsAuthenticated(true);
      setError(null);
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (loginParams?: LoginParams) => {
    if (loginParams) {
      setIsLoading(true);

      // Collect fingerprint data for location verification (optional - graceful degradation)
      let fingerprintData: FingerprintData | null = null;
      try {
        fingerprintData = await getFingerprint();
      } catch (err) {
        console.warn('Failed to collect fingerprint, proceeding without it:', err);
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      // Only add ngrok header in development
      if (import.meta.env.VITE_APP_ENV === 'development') {
        headers['ngrok-skip-browser-warning'] = 'true';
      }

      // Build request body with optional fingerprint data
      const requestBody: {
        code: string;
        redirectUri: string;
        browserLocale?: string;
        fingerprint?: FingerprintData;
      } = {
        code: loginParams.code,
        redirectUri: loginParams.redirectUri,
        browserLocale: loginParams.browserLocale
      };

      // Include fingerprint if available
      if (fingerprintData) {
        requestBody.fingerprint = fingerprintData;
      }

      const res = await fetch(`${config.baseUrl}/${config.endpoints.auth.google.login}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (res.ok) {
        // Extract CSRF token from response header
        const csrfToken = res.headers.get('X-CSRF-Token');
        if (csrfToken) {
          localStorage.setItem(csrfTokenKey, csrfToken);
        }

        const data = await res.json();
        if (data.status === 'success' && data.payload?.accessToken) {
          localStorage.setItem(jwtAuthKey, data.payload.accessToken);
          await verifyToken(data.payload.accessToken);
        }
      } else {
        // Try to parse error response, but handle HTML responses gracefully
        try {
          const data = await res.json();
          if (data.status === 'error' && data.payload?.errorCode === 'geolocation_restricted') {
            setError(data.payload?.errorMessage);
          }
        } catch (e) {
          console.error('Failed to parse error response:', e);
          setError('Login failed');
        }
      }
      setIsLoading(false);
    } else {
      setError('Error logging in');
    }
  }, [ verifyToken, getFingerprint ]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    const token = getIdToken();

    if (token) {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      // Only add ngrok header in development
      if (import.meta.env.VITE_APP_ENV === 'development') {
        headers['ngrok-skip-browser-warning'] = 'true';
      }

      const res = await fetch(`${config.baseUrl}/${config.endpoints.auth.google.logout}`, {
        method: 'GET',
        credentials: 'include',
        headers
      });

      if (res.ok) {
        const data = await res.json();
        if (data.status === 'success') {
          localStorage.removeItem(jwtAuthKey);
          localStorage.removeItem(csrfTokenKey);
          setUser(null);
          setIsAuthenticated(false);
          setError(null);
        }
      } else {
        setError('Error logging out');
      }
    } else {
      setError('Error logging out');
    }
    setIsLoading(false);
  }, []);

  const getIdToken = () => {
    return localStorage.getItem(jwtAuthKey);
  };

  const getCsrfToken = () => {
    return localStorage.getItem(csrfTokenKey);
  };

  const refreshFlow = useCallback(async () => {
    try {
      const accessToken = await refreshToken();
      if (accessToken) {
        await verifyToken(accessToken);
      } else {
        setIsLoading(false);
        setError(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      setIsLoading(false);
      setIsAuthenticated(false);
      setError(err instanceof Error ? err.message : 'Something went wrong');
    }
  }, [verifyToken]);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = getIdToken();
      if (!token) return;

      try {
        const payload = jwtDecode<{ exp: number }>(token);
        const expiresAt = payload.exp * 1000; // Convert to ms
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;

        // Refresh if expiring in less than 5 minutes // 5 * 60 * 1000
        if (timeUntilExpiry < 5 * 60 * 1000) {
          refreshToken();
        }
      } catch (error) {
        console.error('Token parsing error:', error);
      }
    };

    // Check every minute
    // !!!important - checking interval should be shorter than jwt token lifetime // todo: add an error if not
    const interval = setInterval(checkTokenExpiration, config.google.oauth.refreshTokenCheckingInterval);

    refreshFlow(); // refresh on mount

    return () => clearInterval(interval);
  }, [refreshFlow, verifyToken]);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      user,
      login,
      logout,
      getIdToken,
      getCsrfToken,
      error
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}